import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  InputAdornment,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Search as SearchIcon, Science as ScienceIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useEffect, useState, useMemo } from 'react';
import api from '../api/axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Types
interface PressReleaseSummary {
  id: string;
  case_name: string;
  case_number: string | null;
  fraud_type: string;
  healthcare_subtype: string | null;
  settlement_usd: string | null;
  alleged_loss_usd: string | null;
  is_qui_tam: boolean;
  earliest_release_date: string | null;
  latest_release_date: string | null;
  final_posture: string | null;
  primary_victim_agency: string | null;
  state_code: string | null;
  release_count: number;
  in_eval_set: boolean;
  agencies: AgencyRef[];
}

interface AgencyRef {
  agency_id: string;
  abbreviation: string;
  display_name: string;
  description: string;
  government_level: string;
  is_primary: boolean;
  parent_agency: string | null;
}

interface FilterOptions {
  fraud_types: { value: string; label: string; count: number }[];
  victim_agencies: { value: string; abbreviation: string | null; name: string; description: string; government_level: string; count: number }[];
  states: string[];
  districts: { value: string; label: string; count: number }[];
  postures: string[];
}

interface ListResponse {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  eval_set_count: number;
  results: PressReleaseSummary[];
}

// Helpers
const formatMoney = (value: string | null): string => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Decode HTML entities using browser's built-in parser
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};

const fraudTypeColors: Record<string, string> = {
  healthcare: '#1976d2',
  defense: '#388e3c',
  sba: '#f57c00',
  housing: '#7b1fa2',
  customs: '#c62828',
  procurement: '#00796b',
  grants: '#5d4037',
  financial: '#455a64',
  other: '#757575',
};

const fraudTypeLabels: Record<string, string> = {
  healthcare: 'Healthcare',
  defense: 'Defense',
  sba: 'SBA',
  housing: 'Housing',
  customs: 'Customs',
  procurement: 'Procurement',
  grants: 'Grants',
  financial: 'Financial',
  other: 'Other',
};

const fraudTypeDescriptions: Record<string, string> = {
  healthcare: 'Medicare, Medicaid, TRICARE, and other federal healthcare program fraud',
  defense: 'Department of Defense contract and procurement fraud',
  sba: 'Small Business Administration loan and program fraud (PPP, EIDL, etc.)',
  housing: 'HUD, FHA mortgage, and federal housing program fraud',
  customs: 'Customs duties evasion, tariff fraud, and import/export violations',
  procurement: 'Federal contract and procurement fraud across agencies',
  grants: 'Federal grant fraud, misuse of grant funds, false grant applications',
  financial: 'Financial institution fraud, TARP, and banking-related FCA cases',
  other: 'Other False Claims Act violations not in the above categories',
};

export function PressReleaseExploreContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [pressReleases, setPressReleases] = useState<PressReleaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [evalSetCount, setEvalSetCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Filter state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [fraudType, setFraudType] = useState(searchParams.get('fraud_type') || '');
  const [isQuiTam, setIsQuiTam] = useState<string>(searchParams.get('is_qui_tam') || '');
  const [victimAgency, setVictimAgency] = useState(searchParams.get('victim_agency') || '');
  const [state, setState] = useState(searchParams.get('state') || '');
  const [district, setDistrict] = useState(searchParams.get('district') || '');
  const [evalOnly, setEvalOnly] = useState(searchParams.get('in_eval_set') === 'true');

  // Pagination
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(25);

  // Sorting
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'settlement_usd', sort: 'desc' },
  ]);

  // Load filter options
  useEffect(() => {
    api.get('/schemes/explore/press-releases/filters/').then((res) => {
      setFilterOptions(res.data);
    });
  }, []);

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (fraudType) params.set('fraud_type', fraudType);
    if (isQuiTam) params.set('is_qui_tam', isQuiTam);
    if (victimAgency) params.set('victim_agency', victimAgency);
    if (state) params.set('state', state);
    if (district) params.set('district', district);
    if (evalOnly) params.set('in_eval_set', 'true');
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    if (sortModel.length > 0) {
      const sort = sortModel[0];
      params.set('sort', sort.sort === 'desc' ? `-${sort.field}` : sort.field);
    }
    return params.toString();
  }, [search, fraudType, isQuiTam, victimAgency, state, district, evalOnly, page, pageSize, sortModel]);

  // Sync URL with state (without triggering data fetch)
  useEffect(() => {
    setSearchParams(new URLSearchParams(queryParams), { replace: true });
  }, [queryParams, setSearchParams]);

  // Load press releases when query params change
  useEffect(() => {
    const controller = new AbortController();

    const loadPressReleases = async () => {
      setLoading(true);
      try {
        const res = await api.get<ListResponse>(`/schemes/explore/press-releases/?${queryParams}`, {
          signal: controller.signal,
        });
        setPressReleases(res.data.results);
        setTotalCount(res.data.count);
        setEvalSetCount(res.data.eval_set_count);
      } catch (err: any) {
        if (err.name !== 'CanceledError') {
          console.error('Failed to load press releases:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(loadPressReleases, 300);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [queryParams]);

  // Toggle eval set membership
  const handleToggleEval = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/schemes/explore/press-releases/${id}/toggle-eval/`);
      const newValue = res.data.in_eval_set;
      setPressReleases((prev) => prev.map((m) => m.id === id ? { ...m, in_eval_set: newValue } : m));
      setEvalSetCount((prev) => prev + (newValue ? 1 : -1));
    } catch (err) {
      console.error('Failed to toggle eval:', err);
    }
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'case_name',
      headerName: 'Case Name',
      flex: 2,
      minWidth: 300,
      renderCell: (params) => {
        const decodedName = decodeHtmlEntities(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={decodedName} arrow>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={(e) => { e.stopPropagation(); navigate(`/schemes/explore/${params.row.id}`); }}
              >
                {decodedName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'settlement_usd',
      headerName: 'Settlement',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontWeight={600}>
            {formatMoney(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'fraud_type',
      headerName: 'Fraud Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={fraudTypeLabels[params.value] || params.value}
          size="small"
          sx={{
            bgcolor: fraudTypeColors[params.value] || '#757575',
            color: 'white',
          }}
        />
      ),
    },
    {
      field: 'is_qui_tam',
      headerName: 'Qui Tam',
      width: 90,
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          {params.value ? (
            <Chip label="Yes" size="small" color="primary" variant="outlined" />
          ) : (
            <Typography variant="body2" color="text.secondary">No</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'earliest_release_date',
      headerName: 'Date',
      width: 110,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'state_code',
      headerName: 'State',
      width: 70,
      align: 'center',
    },
    {
      field: 'in_eval_set',
      headerName: 'Eval',
      width: 60,
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Tooltip title={params.value ? 'Remove from eval set' : 'Add to eval set'} arrow>
            <IconButton
              size="small"
              onClick={(e) => handleToggleEval(params.row.id, e)}
              sx={{ color: params.value ? 'primary.main' : 'action.disabled' }}
            >
              <ScienceIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <TextField
              placeholder="Search cases or defendants..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              size="small"
              sx={{ minWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Fraud Type */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Fraud Type</InputLabel>
              <Select
                value={fraudType}
                label="Fraud Type"
                onChange={(e: SelectChangeEvent) => { setFraudType(e.target.value); setPage(1); }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.fraud_types.map((ft) => (
                  <MenuItem key={ft.value} value={ft.value}>
                    <Tooltip
                      title={fraudTypeDescriptions[ft.value] || ''}
                      placement="right"
                      arrow
                      enterDelay={300}
                    >
                      <Box component="span" sx={{ width: '100%' }}>
                        {ft.label} ({ft.count})
                      </Box>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Victim Agency */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Defrauded Agency</InputLabel>
              <Select
                value={victimAgency}
                label="Defrauded Agency"
                onChange={(e: SelectChangeEvent) => { setVictimAgency(e.target.value); setPage(1); }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                renderValue={(value) => {
                  if (!value) return 'All';
                  const agency = filterOptions?.victim_agencies.find((va) => va.value === value);
                  return agency?.abbreviation || agency?.value.toUpperCase() || value;
                }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.victim_agencies.map((va) => (
                  <MenuItem key={va.value} value={va.value}>
                    <Tooltip title={va.description} placement="right" arrow enterDelay={500}>
                      <Box sx={{ width: '100%' }}>
                        <Typography component="span" fontWeight={600}>
                          {va.abbreviation || va.value.toUpperCase()}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}
                        >
                          {va.name}
                        </Typography>
                        <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>
                          ({va.count})
                        </Typography>
                      </Box>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* State */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={state}
                label="State"
                onChange={(e: SelectChangeEvent) => { setState(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.states.slice().sort().map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* District */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>District</InputLabel>
              <Select
                value={district}
                label="District"
                onChange={(e: SelectChangeEvent) => { setDistrict(e.target.value); setPage(1); }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.districts?.map((d) => (
                  <MenuItem key={d.value} value={d.value}>{d.label} ({d.count})</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Qui Tam */}
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Qui Tam</InputLabel>
              <Select
                value={isQuiTam}
                label="Qui Tam"
                onChange={(e: SelectChangeEvent) => { setIsQuiTam(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {/* Eval set filter */}
            <Box sx={{ ml: 'auto' }}>
              <Chip
                icon={<ScienceIcon />}
                label={`${evalSetCount} in eval set`}
                size="small"
                variant={evalOnly ? 'filled' : 'outlined'}
                color="primary"
                onClick={() => { setEvalOnly((prev) => !prev); setPage(1); }}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

          </Box>
        </Paper>

        {/* Data Grid */}
        <Paper sx={{ height: 'calc(100vh - 220px)', minHeight: 600 }}>
          <DataGrid
            rows={pressReleases}
            columns={columns}
            loading={loading}
            rowCount={totalCount}
            rowHeight={60}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            paginationModel={{ page: page - 1, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page + 1);
              setPageSize(model.pageSize);
            }}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/schemes/explore/${params.id}`)}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
                borderBottom: '2px solid',
                borderBottomColor: 'divider',
              },
            }}
          />
        </Paper>
    </>
  );
}

export default function PressReleaseExplore() {
  return (
    <Layout>
      <PressReleaseExploreContent />
    </Layout>
  );
}
